import React, { useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import {
  ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  AreaChart, Area, ReferenceLine, ResponsiveContainer,
} from 'recharts'
import { KpiCard } from '../components/KpiCard'
import { ChartCard } from '../components/ChartCard'
import { AnalyticsTable, RateCellRenderer } from '../components/AnalyticsTable'
import {
  KpiRow,
  LogisticsSubTabBar,
  LogisticsSubTab,
} from '../HrAnalytics.styled'
import {
  MOCK_FOOD_KPIS,
  MOCK_FOOD_CHART,
  MOCK_MEAL_TYPE_TABLE,
  MOCK_ACCOMMODATION_KPIS,
  MOCK_ACCOMMODATION_CHART,
  MOCK_CAMP_TABLE,
} from 'features/hrAnalytics/mockData/logistics.mock'

const KpiIcons: Record<string, React.ReactNode> = {
  food: <svg viewBox='0 0 24 24'><path d='M8.1 13.34l2.83-2.83L3.91 3.5a4.008 4.008 0 000 5.66l4.19 4.18zm6.78-1.81c1.53.71 3.68.21 5.27-1.38 1.91-1.91 2.28-4.65.81-6.12-1.46-1.46-4.20-1.10-6.12.81-1.59 1.59-2.09 3.74-1.38 5.27L3.7 19.87l1.41 1.41L12 14.41l6.88 6.88 1.41-1.41L13.41 13l1.47-1.47z' /></svg>,
  checkCircle: <svg viewBox='0 0 24 24'><path d='M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z' /></svg>,
  warning: <svg viewBox='0 0 24 24'><path d='M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z' /></svg>,
  chart: <svg viewBox='0 0 24 24'><path d='M3.5 18.49l6-6.01 4 4L22 6.92l-1.41-1.41-7.09 7.97-4-4L2 16.99z' /></svg>,
  bed: <svg viewBox='0 0 24 24'><path d='M7 13c1.66 0 3-1.34 3-3S8.66 7 7 7s-3 1.34-3 3 1.34 3 3 3zm12-6h-8v7H3V5H1v15h2v-3h18v3h2V10c0-2.21-1.79-4-4-4z' /></svg>,
  person: <svg viewBox='0 0 24 24'><path d='M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z' /></svg>,
}

const getSubTab = (pathname: string): string => {
  if (pathname.includes('/accommodation')) return 'accommodation'
  return 'food'
}

const FOOD_COLUMNS = [
  { key: 'mealType', label: 'Meal Type', sortable: true, width: '20%' },
  { key: 'planned', label: 'Planned', sortable: true, width: '18%' },
  { key: 'served', label: 'Served', sortable: true, width: '18%' },
  { key: 'standbyUsed', label: 'Standby Used', sortable: true, width: '18%' },
  {
    key: 'utilizationRate',
    label: 'Utilization Rate',
    sortable: true,
    width: '26%',
    renderCell: (val: number) => <RateCellRenderer value={val} />,
  },
]

const CAMP_COLUMNS = [
  { key: 'campName', label: 'Camp Name', sortable: true, width: '20%' },
  { key: 'totalBeds', label: 'Total Beds', sortable: true, width: '18%' },
  { key: 'avgOccupied', label: 'Avg Occupied', sortable: true, width: '18%' },
  { key: 'avgAvailable', label: 'Avg Available', sortable: true, width: '18%' },
  {
    key: 'occupancyRate',
    label: 'Occupancy Rate',
    sortable: true,
    width: '26%',
    renderCell: (val: number) => <RateCellRenderer value={val} />,
  },
]

const FoodSubTab = () => {
  const [period, setPeriod] = useState('Day')

  return (
    <>
      <KpiRow>
        {MOCK_FOOD_KPIS.map((kpi, idx) => (
          <KpiCard
            key={idx}
            icon={KpiIcons[kpi.iconKey || '']}
            value={kpi.value}
            label={kpi.label}
            sublabel={kpi.sublabel}
            accentColor={kpi.accentColor}
          />
        ))}
      </KpiRow>

      <ChartCard
        title='Planned vs Served'
        periods={['Day', 'Week']}
        activePeriod={period}
        onPeriodChange={setPeriod}
        legend={[
          { label: 'Planned', color: '#30B6D1' },
          { label: 'Served', color: '#30B6D1' },
          { label: 'Standby Usage', color: '#ff9900' },
        ]}
      >
        <ResponsiveContainer width='100%' height={300}>
          <ComposedChart data={MOCK_FOOD_CHART}>
            <CartesianGrid strokeDasharray='3 3' stroke='#46425520' />
            <XAxis dataKey='day' tick={{ fontSize: 11, fill: '#B0B0B0' }} tickLine={false} />
            <YAxis tick={{ fontSize: 11, fill: '#B0B0B0' }} tickLine={false} />
            <Tooltip />
            <Bar dataKey='planned' fill='#30B6D180' radius={[4, 4, 0, 0]} barSize={20} />
            <Bar dataKey='served' fill='#30B6D1' radius={[4, 4, 0, 0]} barSize={20} />
            <Line type='monotone' dataKey='standbyUsage' stroke='#ff9900' strokeWidth={2} dot={{ r: 3, fill: '#ff9900' }} />
          </ComposedChart>
        </ResponsiveContainer>
      </ChartCard>

      <AnalyticsTable columns={FOOD_COLUMNS} data={MOCK_MEAL_TYPE_TABLE} />
    </>
  )
}

const AccommodationSubTab = () => {
  const [period, setPeriod] = useState('Day')

  return (
    <>
      <KpiRow>
        {MOCK_ACCOMMODATION_KPIS.map((kpi, idx) => (
          <KpiCard
            key={idx}
            icon={KpiIcons[kpi.iconKey || '']}
            value={kpi.value}
            label={kpi.label}
            sublabel={kpi.sublabel}
            accentColor={kpi.accentColor}
          />
        ))}
      </KpiRow>

      <ChartCard
        title='Occupied Beds Over Time'
        periods={['Day', 'Week']}
        activePeriod={period}
        onPeriodChange={setPeriod}
        legend={[
          { label: 'Normal (< 90%)', color: '#00B04C' },
          { label: 'High (90-100%)', color: '#ff9900' },
          { label: 'Over Capacity', color: '#FF4F46' },
        ]}
      >
        <ResponsiveContainer width='100%' height={300}>
          <AreaChart data={MOCK_ACCOMMODATION_CHART}>
            <CartesianGrid strokeDasharray='3 3' stroke='#46425520' />
            <XAxis dataKey='date' tick={{ fontSize: 11, fill: '#B0B0B0' }} tickLine={false} />
            <YAxis tick={{ fontSize: 11, fill: '#B0B0B0' }} tickLine={false} />
            <Tooltip />
            <ReferenceLine
              y={MOCK_ACCOMMODATION_CHART[0]?.capacity || 200}
              stroke='#B0B0B0'
              strokeDasharray='8 8'
              label={{ value: 'Total Bed Capacity', position: 'insideTopLeft', fill: '#B0B0B0', fontSize: 11 }}
            />
            <Area type='monotone' dataKey='overCapacity' stackId='beds' fill='#FF4F46' stroke='#FF4F46' />
            <Area type='monotone' dataKey='high' stackId='beds' fill='#ff9900' stroke='#ff9900' />
            <Area type='monotone' dataKey='normal' stackId='beds' fill='#00B04C' stroke='#00B04C' />
          </AreaChart>
        </ResponsiveContainer>
      </ChartCard>

      <AnalyticsTable columns={CAMP_COLUMNS} data={MOCK_CAMP_TABLE} />
    </>
  )
}

export const LogisticsTab = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const activeSubTab = getSubTab(location.pathname)

  const handleSubTabChange = (key: string) => {
    if (key === 'food') {
      navigate('/hr/analytics/logistics/food')
    } else {
      navigate('/hr/analytics/logistics/accommodation')
    }
  }

  return (
    <>
      <LogisticsSubTabBar>
        <LogisticsSubTab
          active={activeSubTab === 'food'}
          onClick={() => handleSubTabChange('food')}
        >
          Food
        </LogisticsSubTab>
        <LogisticsSubTab
          active={activeSubTab === 'accommodation'}
          onClick={() => handleSubTabChange('accommodation')}
        >
          Accommodation
        </LogisticsSubTab>
      </LogisticsSubTabBar>

      {activeSubTab === 'food' ? <FoodSubTab /> : <AccommodationSubTab />}
    </>
  )
}
